// PrivateKindergartensList.jsx (полная версия)
import React, { useState, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSort,
  faSortUp,
  faSortDown,
  faEllipsisVertical,
  faPlus,
  faEdit,
  faArchive,
  faBoxOpen,
  faTrash,
  faEye,
  faMapMarkerAlt
} from '@fortawesome/free-solid-svg-icons';
import { Alert } from 'react-bootstrap';
import KindergartenModal from './KindergartenModal';
import { useReference } from '../../../kindergartens/detail/components/Dictionary/actions/useReference';
import './style.css';

const PrivateKindergartensList = ({ isEditPossible = true }) => {
  const [activeTab, setActiveTab] = useState('active');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [activeMenuIndex, setActiveMenuIndex] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [viewMode, setViewMode] = useState(false);

  const { 
    data, 
    config, 
    loading, 
    error,
    actions 
  } = useReference('private-kindergartens');

  // Моковые данные для демонстрации
  const kindergartensData = {
    active: [
      {
        _id: 1,
        edrpou: '12345678',
        name: 'ЧДНЗ "Малятко"',
        description: 'Приватний дитячий садочок з розвиваючими заняттями та англійською мовою',
        address: 'м. Київ, вул. Хрещатик, 1',
        district: 'Шевченківський',
        phone: '+380441234567',
        email: 'info@malyatko.kyiv.ua',
        status: 'active',
        createdAt: '2024-01-15',
        updatedAt: '2024-08-20'
      },
      {
        _id: 2,
        edrpou: '87654321',
        name: 'ДНЗ "Сонечко"',
        description: 'Сучасний приватний садочок з басейном та спортивним майданчиком',
        address: 'м. Київ, пр. Перемоги, 25',
        district: 'Солом\'янський',
        phone: '+380449876543',
        email: 'sonechko@ukr.net',
        status: 'active',
        createdAt: '2024-02-10',
        updatedAt: '2024-07-15'
      }
    ],
    archived: []
  };

  const columns = [
    { 
      key: 'edrpou', 
      label: 'ЄДРПОУ', 
      sortable: true, 
      width: '10%' 
    },
    { 
      key: 'name', 
      label: 'Назва закладу', 
      sortable: true, 
      width: '20%' 
    },
    { 
      key: 'description', 
      label: 'Опис закладу', 
      sortable: true, 
      width: '25%' 
    },
    { 
      key: 'address', 
      label: 'Адреса', 
      sortable: true, 
      width: '25%',
      format: (value) => (
        <div>
          <FontAwesomeIcon icon={faMapMarkerAlt} className="me-1 text-muted" />
          {value}
        </div>
      )
    },
    { 
      key: 'district', 
      label: 'Район', 
      sortable: true, 
      width: '10%' 
    },
    { 
      key: 'createdAt', 
      label: 'Дата додавання', 
      sortable: true, 
      width: '10%',
      format: (value) => new Date(value).toLocaleDateString('uk-UA')
    },
    ...(isEditPossible ? [{ key: 'actions', label: 'Дії', sortable: false, width: '10%' }] : [])
  ];

  // Используем моковые данные или данные из API
  const displayData = data.length > 0 ? data : kindergartensData[activeTab];

  // Сортировка
  const sortedData = useMemo(() => {
    if (!sortConfig.key) return displayData;
    
    return [...displayData].sort((a, b) => {
      let aValue = a[sortConfig.key] || '';
      let bValue = b[sortConfig.key] || '';

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [displayData, sortConfig]);

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (columnKey) => {
    if (columnKey !== sortConfig.key) return faSort;
    return sortConfig.direction === 'asc' ? faSortUp : faSortDown;
  };

  const handleMenuToggle = (index) => {
    setActiveMenuIndex(activeMenuIndex === index ? null : index);
  };

  const handleMenuClose = () => {
    setActiveMenuIndex(null);
  };

  const handleActionSelect = async (actionId, item) => {
    try {
      if (actionId === 'edit') {
        setSelectedItem(item);
        setViewMode(false);
        setIsModalOpen(true);
      } else if (actionId === 'view') {
        setSelectedItem(item);
        setViewMode(true);
        setIsModalOpen(true);
      } else if (actionId === 'archive') {
        await actions.archive(item._id);
      } else if (actionId === 'restore') {
        await actions.restore(item._id);
      } else if (actionId === 'delete') {
        if (window.confirm(`Ви впевнені, що хочете назавжди видалити "${item.name}"?`)) {
          await actions.delete(item._id);
        }
      }
    } catch (error) {
      console.error('Помилка при виконанні дії:', error);
      alert(error.message);
    } finally {
      setActiveMenuIndex(null);
    }
  };

  const handleAddItem = () => {
    if (!isEditPossible) return;
    setSelectedItem(null);
    setViewMode(false);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedItem(null);
    setViewMode(false);
  };

  const handleSubmit = async (formData) => {
    try {
      if (selectedItem) {
        await actions.update(selectedItem._id, formData);
      } else {
        await actions.create(formData);
      }
      handleCloseModal();
    } catch (error) {
      alert(`Помилка збереження: ${error.message}`);
    }
  };

  const ActionsMenu = ({ onActionSelect, onClose, item, isArchived }) => {
    const baseActions = [
      { id: 'view', label: 'Переглянути', icon: faEye }
    ];

    const editActions = isArchived 
      ? [
          { id: 'edit', label: 'Редагувати', icon: faEdit },
          { id: 'restore', label: 'Поновити з архіву', icon: faBoxOpen },
          { id: 'delete', label: 'Видалити', icon: faTrash }
        ]
      : [
          { id: 'edit', label: 'Редагувати', icon: faEdit },
          { id: 'archive', label: 'В архів', icon: faArchive }
        ];

    const actions = isEditPossible 
      ? [...baseActions, ...editActions]
      : baseActions;

    return (
      <div className="actions-dropdown">
        {actions.map((action) => (
          <button
            key={action.id}
            className="dropdown-item d-flex align-items-center gap-2 p-2"
            onClick={() => onActionSelect(action.id, item)}
            style={{
              border: 'none',
              backgroundColor: 'transparent',
              width: '100%',
              textAlign: 'left',
              fontSize: '14px',
              cursor: 'pointer'
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
          >
            <FontAwesomeIcon 
              icon={action.icon} 
              style={{ 
                width: '16px', 
                color: action.id === 'delete' ? '#dc3545' : '#6c757d' 
              }} 
            />
            <span style={{ 
              color: action.id === 'delete' ? '#dc3545' : '#212529' 
            }}>
              {action.label}
            </span>
          </button>
        ))}
        
        <div style={{ borderTop: '1px solid #dee2e6', padding: '8px' }}>
          <button
            className="btn btn-sm btn-outline-secondary w-100"
            onClick={onClose}
            style={{ fontSize: '12px' }}
          >
            Скасувати
          </button>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="card queuetable-table-card">
        <div className="card-body p-0">
          {/* Панель табов */}
          <div className="queuetable-year-bar d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center gap-3 flex-wrap">
              <span className="queuetable-label text-white-50">Приватні дитячі садочки</span>
              <ul className="queuetable-year-tabs nav nav-tabs">
                <li className="nav-item">
                  <button
                    className={`nav-link ${activeTab === 'active' ? 'active' : ''}`}
                    onClick={() => setActiveTab('active')}
                  >
                    Активні
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    className={`nav-link ${activeTab === 'archived' ? 'active' : ''}`}
                    onClick={() => setActiveTab('archived')}
                  >
                    Архівні
                  </button>
                </li>
              </ul>
            </div>
            {isEditPossible && (
              <div className="queuetable-switch-wrap">
                <a 
                  href="" 
                  className="link-primary small text-nowrap" 
                  onClick={(e) => { e.preventDefault(); handleAddItem(); }}
                >
                  <FontAwesomeIcon icon={faPlus} className="me-1" />
                  Додати садочок
                </a>
              </div>
            )}
          </div>

          {error && (
            <Alert variant="danger" className="mb-3">
              {error}
            </Alert>
          )}

          {/* Таблица */}
          <div className="table-responsive">
            <table className="table queuetable-table align-middle mb-0">
              <thead>
                <tr>
                  {columns.map((column) => (
                    <th 
                      key={column.key}
                      style={{ width: column.width }}
                      onClick={() => column.sortable && handleSort(column.key)}
                    >
                      <div className="d-flex align-items-center">
                        {column.label}
                        {column.sortable && (
                          <FontAwesomeIcon 
                            icon={getSortIcon(column.key)} 
                            className="ms-1" 
                            style={{ fontSize: '12px' }}
                          />
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={columns.length} className="text-center py-4">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Завантаження...</span>
                      </div>
                      <p className="mt-2 mb-0">Завантаження садочків...</p>
                    </td>
                  </tr>
                ) : sortedData.length > 0 ? (
                  sortedData.map((item, index) => (
                    <tr key={item._id}>
                      {columns.map((column) => {
                        if (column.key === 'actions') {
                          return (
                            <td key="actions" className="queuetable-actions text-end">
                              <div className="actions-menu-container" style={{ position: 'relative', display: 'inline-block' }}>
                                <button
                                  className="btn btn-sm btn-link text-decoration-none p-0"
                                  onClick={() => handleMenuToggle(index)}
                                  style={{ color: '#0069ff' }}
                                  disabled={loading}
                                >
                                  <FontAwesomeIcon icon={faEllipsisVertical} />
                                </button>
                                
                                {activeMenuIndex === index && (
                                  <ActionsMenu 
                                    onActionSelect={handleActionSelect}
                                    onClose={handleMenuClose}
                                    item={item}
                                    isArchived={activeTab === 'archived'}
                                  />
                                )}
                              </div>
                            </td>
                          );
                        }
                        
                        return (
                          <td key={column.key}>
                            {column.format 
                              ? column.format(item[column.key], item)
                              : item[column.key]
                            }
                          </td>
                        );
                      })}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={columns.length} className="text-center py-4">
                      <div className="text-muted">
                        <p className="mb-1">Садочки не знайдено</p>
                        <small>Спробуйте змінити вкладку або додати новий садочок</small>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Пагинация */}
          <div className="d-flex align-items-center mt-4 queuetable-pagination-wrapper">
            <div className="queuetable-pagination-info">
              Всього {sortedData.length} садочків
            </div>
          </div>
        </div>
      </div>

      {/* Модальное окно садика */}
      <KindergartenModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        kindergartenData={selectedItem}
        onSubmit={handleSubmit}
        viewMode={viewMode}
        isEditPossible={isEditPossible}
        loading={loading}
      />
    </>
  );
};

export default PrivateKindergartensList;